import React, { PureComponent } from 'react';
import { dataBuilder, tempDataBuilder, uploadFunctionGetter, GALLERY_FILE_TYPES } from '../Utils/mediaPluginsDataBuilders.js';
import PropTypes from 'prop-types';
import { MediaUploadErrorKey, GALLERY_TYPE, alignmentClassName, sizeClassName, textWrapClassName } from 'wix-rich-content-common';
import { ErrorBoundary } from './ErrorBoundary.js';
import 'wix-rich-content-ui-components';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
const fileReader = (file) => {
  return new Promise((resolve) => {
    resolve(URL.createObjectURL(file));
  });
};
const handleUploadFinished = (type, getComponentData, data, error, onUploadFinished, itemIndex, fileType) => {
  var _a, _b;
  return onUploadFinished({
    data: (_b = (_a = dataBuilder)[type]) == null ? void 0 : _b.call(_a, { data, error }, getComponentData(), fileType, itemIndex),
    error
  });
};
const handleUploadStart = (props, getComponentData, file, onLocalLoad, onUploadFinished, itemPos) => {
  if (file) {
    const { type, commonPubsub } = props;
    fileReader(file).then((url) => {
      var _a, _b;
      const tempData = (_b = (_a = tempDataBuilder)[type]) == null ? void 0 : _b.call(_a, {
        url,
        file
      });
      onLocalLoad == null ? void 0 : onLocalLoad(tempData);
      const handleFileUpload = uploadFunctionGetter[type](props);
      const {
        helpers: { onMediaUploadStart, onMediaUploadEnd }
      } = props;
      const uploadBIData = onMediaUploadStart(type, file.size, file.type);
      const fileType = getGalleryFileType(file.type);
      handleFileUpload(file, ({ data, error }) => {
        onMediaUploadEnd(uploadBIData, error);
        error && commonPubsub.set("onMediaUploadError", error);
        handleUploadFinished(
          type,
          getComponentData,
          data,
          error,
          onUploadFinished,
          itemPos,
          fileType
        );
      });
    }).catch((e) => {
      commonPubsub.set("onMediaUploadError", { key: MediaUploadErrorKey.GENERIC });
      console.error("Reading file locally failed", e);
    });
  }
};
const createBaseMediaPlugin = (PluginComponent) => {
  var _a;
  return _a = class extends PureComponent {
    constructor(props) {
      super(props);
      __publicField(this, "getLoadingParams", (componentState) => {
        var _a2;
        const { isLoading, userSelectedFiles } = componentState;
        return { isLoading: ((_a2 = this.state) == null ? void 0 : _a2.isLoading) || isLoading, userSelectedFiles };
      });
      __publicField(this, "onUploadFinished", ({ data, error }) => {
        const { store, block } = this.props;
        data && store.update("componentData", data, block.getKey());
        let { tempData } = this.state;
        if (!error) {
          tempData = null;
        }
        this.setState((state) => {
          const itemsLeftToUpload = state.itemsLeftToUpload - 1;
          const isLoading = itemsLeftToUpload > 0;
          !isLoading && store.update("componentState", { isLoading: false, userSelectedFiles: null });
          return { itemsLeftToUpload, isLoading, tempData };
        });
      });
      __publicField(this, "onLocalLoad", (tempData) => this.setState({ tempData }));
      __publicField(this, "handleFilesSelected", (files) => {
        Array(...files).forEach((file) => {
          handleUploadStart(
            this.props,
            this.getComponentData,
            file,
            this.onLocalLoad,
            this.onUploadFinished
          );
        });
      });
      __publicField(this, "handleFilesAdded", ({ data, error }) => {
        const { type } = this.props;
        if (data instanceof Array) {
          data.forEach((item, index) => {
            handleUploadFinished(
              type,
              this.getComponentData,
              item,
              error instanceof Array && error[index] || error,
              this.onUploadFinished
            );
          });
        } else {
          handleUploadFinished(type, this.getComponentData, data, error, this.onUploadFinished);
        }
      });
      __publicField(this, "getComponentData", () => this.props.componentData);
      this.state = { isLoading: false, tempData: null };
    }
    componentDidMount() {
      const { block, store, commonPubsub, type } = this.props;
      const blockKey = block.getKey();
      store == null ? void 0 : store.setBlockHandler("handleFilesSelected", blockKey, this.handleFilesSelected.bind(this));
      store == null ? void 0 : store.setBlockHandler("handleFilesAdded", blockKey, this.handleFilesAdded.bind(this));
      type === GALLERY_TYPE && (commonPubsub == null ? void 0 : commonPubsub.setBlockHandler(
        "galleryHandleFilesAdded",
        blockKey,
        this.handleFilesAdded.bind(this)
      ));
      this.updateComponent();
    }
    componentDidUpdate(_, prevState) {
      this.updateComponent();
      const { type, store, block, componentData } = this.props;
      if (type === GALLERY_TYPE) {
        if (prevState.isLoading && !this.state.isLoading) {
          store.set("componentData", __spreadProps(__spreadValues({}, componentData), { loading: void 0 }), block.getKey());
        } else if (this.state.isLoading && !prevState.isLoading) {
          store.set("componentData", __spreadProps(__spreadValues({}, componentData), { loading: true }), block.getKey());
        }
      }
    }
    updateComponent() {
      const componentState = this.props.componentState || {};
      const { isLoading, userSelectedFiles } = this.getLoadingParams(componentState);
      if (!isLoading && userSelectedFiles) {
        if (userSelectedFiles.files && userSelectedFiles.files.length > 0) {
          this.setState(
            { itemsLeftToUpload: userSelectedFiles.files.length, isLoading: true },
            () => this.handleFilesSelected(userSelectedFiles.files)
          );
        }
        setTimeout(() => {
          this.props.store.update("componentState", { isLoading: true, userSelectedFiles: null });
        }, 0);
      }
    }
    render() {
      const { isLoading, tempData } = this.state;
      return /* @__PURE__ */ React.createElement(ErrorBoundary, { t: this.props.t }, /* @__PURE__ */ React.createElement(PluginComponent, __spreadProps(__spreadValues({}, this.props), { isLoading, tempData })));
    }
  }, __publicField(_a, "propTypes", {
    componentData: PropTypes.object.isRequired,
    componentState: PropTypes.object.isRequired,
    store: PropTypes.object.isRequired,
    block: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
    commonPubsub: PropTypes.object,
    type: PropTypes.string
  }), __publicField(_a, "alignmentClassName", (...args) => {
    var _a2;
    const alignment = (_a2 = PluginComponent.alignmentClassName) == null ? void 0 : _a2.call(PluginComponent, ...args);
    return typeof alignment === "string" ? alignment : alignmentClassName(...args);
  }), __publicField(_a, "sizeClassName", (...args) => {
    var _a2;
    const size = (_a2 = PluginComponent.sizeClassName) == null ? void 0 : _a2.call(PluginComponent, ...args);
    return typeof size === "string" ? size : sizeClassName(...args);
  }), __publicField(_a, "textWrapClassName", (...args) => {
    var _a2;
    const textWrap = (_a2 = PluginComponent.textWrapClassName) == null ? void 0 : _a2.call(PluginComponent, ...args);
    return typeof textWrap === "string" ? textWrap : textWrapClassName(...args);
  }), _a;
};
const getGalleryFileType = (type) => {
  return type.match("image/*") ? GALLERY_FILE_TYPES.IMAGE : type.match("video/*") ? GALLERY_FILE_TYPES.VIDEO : "";
};

export { createBaseMediaPlugin, handleUploadFinished, handleUploadStart };
//# sourceMappingURL=createBaseMediaPlugin.js.map
